import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import { AppIcon } from "@/components/AppIcon";
import { Button } from "@/components/ui/Button";
import { REASON_MAX, REASON_MIN, useRequestAccess, type DirectoryApp } from "@/lib/apps";

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function RequestDialog({ app, onClose }: { app: DirectoryApp; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const request = useRequestAccess();
  const trimmed = reason.trim().length;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Keeps the directory behind from scrolling under the dialog on a phone.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  // Portalled to <body> for the same reason as the feedback dialog: an ancestor
  // with a backdrop-filter would otherwise become the containing block for `fixed`.
  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Request access to ${app.title}`}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border-border max-h-[85dvh] w-full max-w-lg overflow-y-auto rounded-lg border p-5 shadow-lg"
      >
        <div className="mb-4 flex items-center gap-3">
          <AppIcon title={app.title} iconPath={app.iconPath} size={32} />
          <h2 className="font-semibold">Request access to {app.title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="hover:bg-accent ml-auto -m-1 rounded p-1"
          >
            <X size={16} />
          </button>
        </div>

        {request.isSuccess ? (
          <>
            <p className="bg-accent text-accent-foreground rounded-md px-3 py-2 text-sm">
              Sent to the admin team, with a copy to your email. Once you’ve been added,{" "}
              {app.title} will appear with your other apps.
            </p>
            <div className="mt-4 flex justify-end">
              <Button size="sm" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              request.mutate({ app, reason: reason.trim() });
            }}
          >
            <div>
              <label htmlFor="ar-reason" className="mb-1.5 block text-sm font-medium">
                Why do you need access? <span className="text-destructive">*</span>
              </label>
              <textarea
                id="ar-reason"
                required
                autoFocus
                rows={5}
                minLength={REASON_MIN}
                maxLength={REASON_MAX}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="What you'll use it for, and who can vouch for it."
                className="border-input bg-card text-foreground placeholder:text-muted-foreground w-full rounded-md border px-3 py-2 text-sm"
              />
              <p className="text-muted-foreground mt-1 flex justify-between text-xs">
                <span>The admin team gets this by email; you get a copy.</span>
                <span className="tabular-nums">
                  {reason.length.toLocaleString()} / {REASON_MAX.toLocaleString()}
                </span>
              </p>
            </div>

            {/* What was typed is kept on failure — it only goes once it is sent. */}
            {request.error && (
              <p className="text-destructive text-sm">
                {request.error instanceof Error
                  ? request.error.message
                  : "Could not send."}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={request.isPending || trimmed < REASON_MIN}
              >
                {request.isPending ? "Sending…" : "Send request"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * An app you cannot use, listed because a superadmin opted it in.
 *
 * ⚠ Not a link. Every other card on the page opens its app; this one would only
 * bounce off that app's gate, so the whole card is the description and one button.
 */
export function RequestableAppCard({ app }: { app: DirectoryApp }) {
  const [open, setOpen] = useState(false);
  // Stable, so the dialog's key and scroll-lock effect runs once per opening.
  const close = useCallback(() => setOpen(false), []);

  return (
    <div className="border-border bg-card flex gap-3 rounded-lg border p-4">
      <AppIcon title={app.title} iconPath={app.iconPath} />

      <div className="flex min-w-0 flex-1 flex-col">
        <p className="text-card-foreground font-medium">{app.title}</p>
        {app.description ? (
          <p className="text-muted-foreground mt-1 text-sm">{app.description}</p>
        ) : (
          <p className="text-muted-foreground/70 mt-1 text-sm italic">
            No description yet
          </p>
        )}

        <div className="mt-3">
          {app.requestedAt ? (
            <p className="text-muted-foreground inline-flex items-center gap-1.5 text-sm">
              <Check size={14} className="text-success" />
              Requested on {shortDate(app.requestedAt)}
            </p>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
              Request access
            </Button>
          )}
        </div>
      </div>

      {open && <RequestDialog app={app} onClose={close} />}
    </div>
  );
}
